import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { BookOpen } from "lucide-react";
import TextSection from "../BiblicalDefense/components/TextSection";
import ScriptureSection from "../BiblicalDefense/components/ScriptureSection";
import ObjectionsSection from "../BiblicalDefense/components/ObjectionsSection";
import ResponseActions from "./ResponseActions";
import { AtheismResponse } from "./types";

interface AtheismResponseProps {
  response: AtheismResponse;
  isSaving?: boolean;
  onSave?: () => void;
  onShareEmail?: () => void;
  onShareSMS?: () => void;
  onShareFacebook?: () => void;
}

const ResponseDisplay: React.FC<AtheismResponseProps> = ({ 
  response,
  isSaving = false,
  onSave = () => {},
  onShareEmail = () => {},
  onShareSMS = () => {},
  onShareFacebook = () => {}
}) => {
  // Ensure we have a response object
  if (!response) {
    return (
      <Card className="border-heaven-gold/10 mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-heaven-gold">
            <BookOpen className="h-5 w-5" />
            Error: No Response
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>No response data was received. Please try your question again.</p>
        </CardContent>
      </Card>
    );
  }

  // Adapter function to handle potentially missing fields with graceful defaults
  const adaptResponse = (data: any): AtheismResponse => {
    return {
      title: typeof data.title === 'string' ? data.title : "Biblical Response to Atheist Challenge",
      directAnswer: typeof data.directAnswer === 'string' ? data.directAnswer : "",
      biblicalFoundation: typeof data.biblicalFoundation === 'string' ? data.biblicalFoundation : "",
      scriptureReferences: Array.isArray(data.scriptureReferences) ? data.scriptureReferences : [],
      theologicalReasoning: typeof data.theologicalReasoning === 'string' ? data.theologicalReasoning : "",
      historicalContext: typeof data.historicalContext === 'string' ? data.historicalContext : "",
      objections: typeof data.objections === 'string' ? data.objections : "",
      finalEncouragement: typeof data.finalEncouragement === 'string' ? data.finalEncouragement : ""
    };
  };

  // Apply adapter to ensure consistent structure
  const adaptedResponse = adaptResponse(response);
  
  // Clean the final encouragement to prevent duplicated content
  const cleanFinalEncouragement = adaptedResponse.finalEncouragement
    ? cleanDuplicatedContent(adaptedResponse.finalEncouragement)
    : "";
  
  // Clean the objections to prevent duplicated content
  const cleanObjections = adaptedResponse.objections
    ? cleanDuplicatedContent(adaptedResponse.objections)
    : "";
  
  // Determine if we should use the structured objections component
  // Enhanced check to better identify structured objection formats
  const hasStructuredObjections = 
    cleanObjections && 
    (cleanObjections.includes("Objection 1:") || 
     cleanObjections.includes("Objection:") ||
     cleanObjections.includes("Response:") ||
     (cleanObjections.includes("Objection") && cleanObjections.includes("Response")));

  // Remove objection content from historical context if it's duplicated in the objections section
  const cleanHistoricalContext = adaptedResponse.historicalContext && cleanObjections 
    ? removeOverlappingObjections(adaptedResponse.historicalContext, cleanObjections)
    : adaptedResponse.historicalContext;

  return (
    <Card className="border-heaven-gold/10 mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-heaven-gold">
          <BookOpen className="h-5 w-5" />
          {adaptedResponse.title}
        </CardTitle>
      </CardHeader>

      {/* Add ResponseActions at the top */}
      <div className="px-6">
        <ResponseActions
          onSave={onSave}
          onShareEmail={onShareEmail}
          onShareSMS={onShareSMS}
          onShareFacebook={onShareFacebook}
          isSaving={isSaving}
        />
      </div>

      <CardContent className="space-y-8">
        {/* Direct Answer Section - only show if we have content */}
        {adaptedResponse.directAnswer && (
          <TextSection 
            number={1} 
            title="Direct Answer" 
            content={adaptedResponse.directAnswer} 
          />
        )}
        
        {/* Biblical Foundation Section - only show if we have content */}
        {adaptedResponse.biblicalFoundation && (
          <TextSection 
            number={2} 
            title="Biblical Foundation" 
            content={adaptedResponse.biblicalFoundation} 
          />
        )}
        
        {/* Scripture References Section - only show if we have references */}
        {adaptedResponse.scriptureReferences && adaptedResponse.scriptureReferences.length > 0 && (
          <ScriptureSection scriptureReferences={adaptedResponse.scriptureReferences} />
        )}
        
        {/* Theological Reasoning Section - only show if we have content */}
        {adaptedResponse.theologicalReasoning && (
          <TextSection 
            number={4} 
            title="Logical, Philosophical, and Moral Reasoning" 
            content={adaptedResponse.theologicalReasoning} 
          />
        )}
        
        {/* Historical Context Section - only show if we have content and it's not duplicated in objections */}
        {cleanHistoricalContext && (
          <TextSection 
            number={5} 
            title="Historical and Scientific Support" 
            content={cleanHistoricalContext} 
          />
        )}
        
        {/* Common Objections Section - only show if we have content */}
        {cleanObjections && (
          hasStructuredObjections ? (
            <ObjectionsSection objections={cleanObjections} />
          ) : (
            <TextSection 
              number={6} 
              title="Addressing Common Objections" 
              content={cleanObjections} 
            />
          )
        )}
        
        {/* Final Encouragement Section - only show if we have content */}
        {cleanFinalEncouragement && (
          <TextSection 
            number={7} 
            title="Final Encouragement" 
            content={cleanFinalEncouragement} 
            isLast={true}
          />
        )}
      </CardContent>
      
      {/* Add ResponseActions at the bottom too */}
      <div className="px-6 pb-6">
        <ResponseActions
          onSave={onSave}
          onShareEmail={onShareEmail}
          onShareSMS={onShareSMS}
          onShareFacebook={onShareFacebook}
          isSaving={isSaving}
        />
      </div>
    </Card>
  );
};

/**
 * Remove duplicate content and clean up formatting
 */
function cleanDuplicatedContent(text: string): string {
  if (!text) return '';
  
  // Split by paragraphs and filter out duplicates
  const paragraphs = text.split(/\n+/).filter(Boolean);
  const uniqueParagraphs = [];
  const seenParagraphs = new Set();
  
  // First check for obvious duplicate sections (same markers appearing multiple times)
  const sectionMarkers = text.match(/(?:Objection\s+\d+:|Objection:|Response:|Final Encouragement:)/gi) || [];
  const uniqueMarkers = [...new Set(sectionMarkers.map(m => m.toLowerCase()))];
  
  // If we detect duplicate markers, try to clean up the text
  if (sectionMarkers.length > uniqueMarkers.length) {
    // Try to find where the first complete set ends
    const markerPositions = [];
    let lastPosition = -1;
    
    for (const marker of uniqueMarkers) {
      const regex = new RegExp(marker, 'gi');
      const matches = Array.from(text.matchAll(regex));
      
      if (matches.length > 1) {
        // We found a duplicated marker, use its second occurrence as a cutoff point
        const secondPos = matches[1].index;
        if (secondPos && secondPos > lastPosition) {
          lastPosition = secondPos;
          markerPositions.push(secondPos);
        }
      }
    }
    
    // If we found potential duplication points, use the earliest one as cutoff
    if (markerPositions.length > 0) {
      const cutoffPoint = Math.min(...markerPositions);
      // Only use the text up to the first duplication point
      const cleanedText = text.substring(0, cutoffPoint).trim();
      if (cleanedText.length > 50) {  // Ensure we have substantial content
        return cleanedText;
      }
    }
  }
  
  // If we can't detect an obvious duplication pattern, use fingerprinting approach
  for (const paragraph of paragraphs) {
    // Create a fingerprint by normalizing the text
    const normalized = paragraph.toLowerCase()
      .replace(/\d+/g, '')  // Remove numbers
      .replace(/\*\*/g, '')  // Remove markdown
      .replace(/[^\w\s]/g, '')  // Remove punctuation
      .replace(/\s+/g, ' ')  // Normalize whitespace
      .trim();
    
    if (!seenParagraphs.has(normalized) && normalized.length > 0) {
      seenParagraphs.add(normalized);
      uniqueParagraphs.push(paragraph);
    }
  }
  
  return uniqueParagraphs.join('\n\n');
}

/**
 * Remove objection content from historical context to prevent duplication
 */
function removeOverlappingObjections(historicalContext: string, objections: string): string {
  if (!historicalContext || !objections) return historicalContext;
  
  // Extract key phrases from objections (first 8 words of each objection)
  const objectionPhrases: string[] = [];
  const objectionMatches = objections.match(/Objection(?:\s+\d+)?:\s*(.*?)(?=\n|$)/gi);
  
  if (objectionMatches) {
    for (const match of objectionMatches) {
      const content = match.replace(/Objection(?:\s+\d+)?:\s*/i, '').trim();
      const firstFewWords = content.split(/\s+/).slice(0, 8).join(' ').toLowerCase();
      if (firstFewWords.length > 10) {
        objectionPhrases.push(firstFewWords);
      }
    }
  }
  
  // If we don't have any phrases to check, return the original context
  if (objectionPhrases.length === 0) return historicalContext;
  
  // Split historical context into paragraphs
  const paragraphs = historicalContext.split(/\n\s*\n/);
  const filteredParagraphs = [];
  
  // Keep paragraphs that don't overlap with objection content
  for (const paragraph of paragraphs) {
    const lowerParagraph = paragraph.toLowerCase();
    let shouldKeep = true;
    
    for (const phrase of objectionPhrases) {
      if (lowerParagraph.includes(phrase)) {
        shouldKeep = false;
        break;
      }
    }
    
    // Also check if paragraph looks like an objection
    if (shouldKeep && (
        lowerParagraph.includes('objection') || 
        lowerParagraph.includes('atheist argues') ||
        lowerParagraph.includes('skeptic claims') ||
        lowerParagraph.includes('response to') ||
        lowerParagraph.includes('rebuttal'))) {
      shouldKeep = false;
    }
    
    if (shouldKeep) {
      filteredParagraphs.push(paragraph);
    }
  }
  
  // If we removed all paragraphs, return a short message
  if (filteredParagraphs.length === 0) {
    return "See the 'Addressing Common Objections' section for relevant historical and scientific information related to objections.";
  }
  
  return filteredParagraphs.join('\n\n');
}

export default ResponseDisplay;
